import { _ } from "../../../polyglot/polyglot.js";
import {
	accountOperationsLabels,
	nameLabel,
} from "../../../../models/wysiwygEditor/builders/helpers.js";
import { periodSectionRenderer } from "./popoverPeriodSectionBuilder.js";
import { select, spacer } from "../../../elements/index.js";
import displaySectionBuilder from "./displaySectionBuilder.js";
import makeInputValuePopover from "./makeInputValuePopover.js";
import precisionSectionBuilder from "./precisionSectionBuilder.js";
import regionWidget from "../../regionWidget.js";
import SwitchButton from "../../SwitchButton/SwitchButton.js";

/**
 * Create a popover that shows when clicking on an account input value.
 *
 * @param {function} getPeriod - Function returning the base period used to fetch data
 * @param {bool} isBimesterEnabled - When true, the period type dropdown shows Bimester
 * @param {PeriodType} defaultPeriodType - Period type used by default
 * @param {function} updateNodeAttributes - Function used to update a ProseMirror node attributes
 * @param {function():DOMElement} getTriggerItem - Returns the element
 *   that will trigger the popover on click
 * @param {object} content - Value fetched by the input value
 * @param {KeyFigure} content.account - Fetched account
 * @param {string} content.precision - Precision to use to format the Keyfigure value
 * @param {string} content.display - What to display, name, value or both
 * @param {number} content.shift - Number of period shifts
 * @param {PeriodType} content.shiftPeriodType - Type of period shift
 * @param {PeriodType} content.periodType - Fetched period type
 */
export default function makeAccountInputValuePopover({
	getPeriod,
	isBimesterEnabled,
	defaultPeriodType,
	updateNodeAttributes,
	getTriggerItem,
	content: {
		account,
		showAccountNumber,
		precision,
		display,
		shift,
		shiftPeriodType,
		periodType,
		switchSign,
		valueType,
	},
}) {
	let localDisplay = display;

	const currencyPrecisionRegion = precisionSectionBuilder({
		shouldShowFn: () => localDisplay !== nameLabel,
		precision,
		onChange: (value) => {
			updateNodeAttributes({
				precision: value,
			});
		},
	});

	const periodSection = periodSectionRenderer({
		isBimesterEnabled,
		shouldShowFn: () => localDisplay !== nameLabel,
		shift,
		shiftPeriodType,
		updateNodeAttributes,
		defaultPeriodType,
		periodType,
		canShift: !getPeriod().isConsecutiveMonths(),
	});

	const valueTypeSection = valueTypeSectionRenderer({
		valueType,
		shouldShowFn: () => localDisplay !== nameLabel,
		onChange: (selection) => {
			if (!selection) {
				return;
			}

			updateNodeAttributes({ valueType: selection });
		},
	});

	return makeInputValuePopover({
		triggerItem: getTriggerItem(),
		header: (html) => {
			html.span(html.strong(account.getName()), " - ").renderNonBreaking(
				getPeriod().toSmartString(),
			);
		},
		content: (html) => {
			html.div(
				displaySectionBuilder({
					display,
					onChange: (value) => {
						updateNodeAttributes({
							display: value,
						});

						localDisplay = value;
						currencyPrecisionRegion.update();
						periodSection.update();
					},
				}),
				accountNumberCheckboxRenderer({
					checked: showAccountNumber,
					onChange: (checked) => {
						updateNodeAttributes({
							showAccountNumber: checked,
						});
					},
				}),
				valueTypeSection,
				currencyPrecisionRegion,
				switchSignCheckboxRenderer({
					checked: switchSign,
					onChange: (checked) => {
						updateNodeAttributes({
							switchSign: checked,
						});
					},
				}),
				periodSection,
			);
		},
	});
}

function valueTypeSectionRenderer({
	valueType,
	onChange,
	shouldShowFn = () => true,
} = {}) {
	const items = Object.entries(accountOperationsLabels).map(
		([key, value]) => ({
			label: value,
			value: key,
		}),
	);

	return regionWidget({
		content: (html) => {
			const shouldShow = shouldShowFn();

			html.div(
				{
					class: [
						"value-type",
						"popover-section",
						{ disabled: !shouldShow },
					],
				},
				html.div(
					{ klass: "right-menu-block", "data-test": "value-type" },
					html.span({ klass: "switch-label" }, _("Value type")),
					select({ items, selected: valueType, onChange }),
				),
			);
		},
	});
}

function accountNumberCheckboxRenderer({ checked, onChange }) {
	return makeSwitchRenderer({
		checked,
		onChange,
		label: _("Show account number"),
		name: "show-account-number",
	});
}

function switchSignCheckboxRenderer({ checked, onChange }) {
	return makeSwitchRenderer({
		checked,
		onChange,
		label: _("Credit Positive"),
		name: "switch-sign",
	});
}

function makeSwitchRenderer({ checked, onChange, label, name }) {
	const switchButton = new SwitchButton({
		state: checked,
		dataTestName: name,
	});

	switchButton.stateChanged.register(onChange);

	return spacer(
		{ as: "label", spacing: "between", top: true },
		label,
		switchButton,
	);
}
